import React, { useState, useEffect } from 'lib/react';
import {
  EVGrid, EVTypography,
} from 'lib/components';
import PropTypes from 'prop-types';
import getPropertDataLatestImageBlobService from 'services/getPropertDataLatestImageBlob.service';
import getImageBlobService from 'services/getImageBlob.service';
import { useSelector } from 'lib/redux';
import PropertyDataDisplayStyles from './PropertyDataDisplayStyles';

function PropertyDataImages(props) {
  const {
    imageryData,
    evImageLatest,
  } = props;
  const classes = PropertyDataDisplayStyles();

  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imagesError, setImagesError] = useState(null);
  const [evImageLatestURL, setEvImageLatestURL] = useState(null);

  const geocodedAddress = useSelector(({ orderAddresses }) => orderAddresses.geocodedAddress);

  // Extract and fetch images from property data result
  useEffect(() => {
    const fetchImages = async () => {
      if (!imageryData) return;

      setImagesLoading(true);
      setImagesError(null);

      try {
        const imagePromises = Object.entries(imageryData).map(async ([key, imageData]) => {
          const { image_token: imageToken } = imageData;
          if (!imageToken) return null;

          const result = await getImageBlobService(imageToken);
          if (result.ok) {
            return {
              id: key,
              imageUrl: result.imageUrl,
              metadata: imageData.metadata,
              token: imageToken,
            };
          }
          return null;
        });

        const imageResults = await Promise.all(imagePromises);
        const validImages = imageResults.filter(Boolean);

        setImages(validImages);

        if (evImageLatest?.urn) {
          const evImageBlob = await getPropertDataLatestImageBlobService(
            { urn: evImageLatest.urn, longitude: geocodedAddress.longitude, latitude: geocodedAddress.latitude },
          );
          setEvImageLatestURL(evImageBlob?.imageUrl);
        }
      } catch (error) {
        setImagesError('Failed to load images');
      } finally {
        setImagesLoading(false);
      }
    };

    fetchImages();
  }, [imageryData]);

  // Cleanup image URLs on unmount
  useEffect(() => () => {
    images.forEach((image) => {
      if (image.imageUrl) {
        URL.revokeObjectURL(image.imageUrl);
      }
    });
  }, [images]);

  useEffect(() => {
    if (evImageLatestURL) {
      URL.revokeObjectURL(evImageLatestURL);
    }
  }, [evImageLatest]);

  if (imagesLoading) {
    return (
      <EVTypography className={classes.dataValue}>
        Loading images...
      </EVTypography>
    );
  }

  if (imagesError) {
    return (
      <EVTypography className={classes.dataValue} style={{ color: 'red' }}>
        {imagesError}
      </EVTypography>
    );
  }

  return (
    <>
      {/* Property Images Section */}
      <EVGrid container spacing={2} style={{ marginTop: '8px' }}>
        {evImageLatest && (
        <div style={{ textAlign: 'center' }}>
          <img
            src={evImageLatestURL}
            alt="EV Latest"
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '4px',
              marginBottom: '8px',
            }}
          />
          <EVTypography variant="caption" display="block">
            Latest Imagery
          </EVTypography>
          <EVTypography variant="caption" display="block" style={{ color: '#666' }}>
            Shot Date:
            {new Date(evImageLatest.shotTime).toLocaleDateString('en-US')}
          </EVTypography>
        </div>
        )}
        {images
          .sort((a, b) => {
            // Sort by shot_date, most recent first
            const dateA = a.metadata?.shot_date ? new Date(a.metadata.shot_date) : new Date(0);
            const dateB = b.metadata?.shot_date ? new Date(b.metadata.shot_date) : new Date(0);
            return dateB.getTime() - dateA.getTime();
          })
          .map((image) => (
            <EVGrid item xs={12} sm={6} key={image.id}>
              <div style={{ textAlign: 'center' }}>
                <img
                  src={image.imageUrl}
                  alt={image.metadata?.description || 'Property image'}
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    height: 'auto',
                    borderRadius: '4px',
                    marginBottom: '8px',
                  }}
                />
                <EVTypography variant="caption" display="block">
                  {image.metadata?.description}
                </EVTypography>
                {image.metadata?.shot_date && (
                <EVTypography variant="caption" display="block" style={{ color: '#666' }}>
                  Shot Date:
                  {image.metadata.shot_date}
                </EVTypography>
                )}
                {image.metadata?.view && (
                <EVTypography variant="caption" display="block" style={{ color: '#666' }}>
                  View:
                  {image.metadata.view}
                </EVTypography>
                )}
              </div>
            </EVGrid>
          ))}
      </EVGrid>
    </>
  );
}

PropertyDataImages.propTypes = {
  imageryData: PropTypes.shape().isRequired,
  evImageLatest: PropTypes.shape().isRequired,
};

export default PropertyDataImages;
